import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { pageTransition, formatDate } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import {
  downloadDocument,
  listAccessLogs,
  listDocumentVersions,
  listDocuments,
  signDocument,
  uploadDocument,
  uploadNewVersion,
} from "@/api/documents";
import { listEmployees } from "@/api/employees";
import type { EmployeeListItem } from "@/types/employee";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { SavedViewsMenu } from "@/components/shared/SavedViewsMenu";
import { useSavedViews } from "@/hooks/useSavedViews";
import { MySignaturesPanel } from "@/components/documents/MySignaturesPanel";
import {
  FileText,
  Upload,
  PenTool,
  AlertTriangle,
  Download,
  History,
  Eye,
} from "lucide-react";
import type {
  Document,
  DocumentAccessLog,
  DocumentVersion,
} from "@/types/document";
import { DOC_TYPE_LABELS } from "@/types/document";
import type { PaginatedResponse } from "@/types/common";

/**
 * Turn an axios/FastAPI error into a single actionable sentence. Mirrors
 * the helper on EmployeeCreatePage — pydantic returns
 * ``detail: [{loc, msg}, …]`` for validation errors and a plain string
 * for everything else.
 */
function humanizeUploadError(err: unknown): string {
  const resp = (err as { response?: { data?: { detail?: unknown } } })?.response;
  const detail = resp?.data?.detail;
  if (Array.isArray(detail)) {
    const first = detail[0] as { loc?: unknown[]; msg?: string } | undefined;
    if (first?.msg) {
      const field = String(first.loc?.[first.loc.length - 1] ?? "");
      return field ? `${field.replace(/_/g, " ")}: ${first.msg}` : first.msg;
    }
  }
  if (typeof detail === "string") return detail;
  return "Couldn't upload the document. Please check the file and try again.";
}

export function DocumentsPage() {
  const { can } = usePermissions();
  const { addToast } = useToast();

  const [data, setData] = useState<PaginatedResponse<Document> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");

  const savedViews = useSavedViews<{ typeFilter: string }>({
    scope: "documents",
    filters: { typeFilter },
    defaultFilters: { typeFilter: "" },
    onApply: (f) => {
      setTypeFilter(f.typeFilter);
      setPage(1);
    },
  });
  const [showUpload, setShowUpload] = useState(false);

  // Upload form
  const [uploadName, setUploadName] = useState("");
  const [uploadType, setUploadType] = useState("other");
  const [uploadEmployeeId, setUploadEmployeeId] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadSig, setUploadSig] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Version history
  const [versionDoc, setVersionDoc] = useState<Document | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [newVersionFile, setNewVersionFile] = useState<File | null>(null);

  // Access logs
  const [logsDoc, setLogsDoc] = useState<Document | null>(null);
  const [logs, setLogs] = useState<DocumentAccessLog[]>([]);

  // Employee pool for the upload picker. Loaded lazily when the modal
  // opens so we don't pay for it for employees who never upload.
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!showUpload || employees.length > 0) return;
    let cancelled = false;
    // Backend caps per_page at 100 — anything larger 422s and the
    // picker stays empty.
    listEmployees(1, 100, { status: "ACTIVE" })
      .then((page) => {
        if (!cancelled) setEmployees(page.items);
      })
      .catch((err) => {
        // Log so an empty picker isn't silent — previously masked a 422
        // from per_page=200. Keep non-fatal so the rest of the modal
        // stays usable if the directory fetch fails.
        console.error("DocumentsPage: listEmployees failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, [showUpload, employees.length]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listDocuments(
        page,
        20,
        undefined,
        typeFilter || undefined
      );
      setData(result);
    } catch {
      addToast("error", "Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  }, [page, typeFilter, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSign = async (docId: string) => {
    try {
      await signDocument(docId);
      addToast("success", "Document signed successfully");
      fetchData();
    } catch {
      addToast("error", "Failed to sign document");
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      await downloadDocument(doc.id, doc.name);
    } catch {
      addToast("error", "Failed to download document");
    }
  };

  const handleUpload = async () => {
    setUploadError(null);
    if (!uploadFile) {
      setUploadError("Please choose a file to upload.");
      return;
    }
    if (!uploadName.trim()) {
      setUploadError("Document name is required.");
      return;
    }
    if (!uploadEmployeeId) {
      setUploadError("Please select the employee this document belongs to.");
      return;
    }
    setIsUploading(true);
    try {
      await uploadDocument(
        uploadEmployeeId,
        uploadName,
        uploadType,
        uploadFile,
        uploadSig
      );
      addToast("success", "Document uploaded");
      setShowUpload(false);
      resetUploadForm();
      fetchData();
    } catch (err) {
      setUploadError(humanizeUploadError(err));
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadName("");
    setUploadType("other");
    setUploadEmployeeId("");
    setUploadFile(null);
    setUploadSig(false);
    setUploadError(null);
  };

  const openVersions = async (doc: Document) => {
    setVersionDoc(doc);
    setVersions([]);
    setNewVersionFile(null);
    try {
      const v = await listDocumentVersions(doc.id);
      setVersions(v);
    } catch {
      addToast("error", "Failed to load versions");
    }
  };

  const uploadVersion = async () => {
    if (!versionDoc || !newVersionFile) return;
    try {
      await uploadNewVersion(versionDoc.id, newVersionFile);
      addToast("success", "New version uploaded");
      setNewVersionFile(null);
      await openVersions(versionDoc);
      fetchData();
    } catch {
      addToast("error", "Failed to upload version");
    }
  };

  const openLogs = async (doc: Document) => {
    setLogsDoc(doc);
    setLogs([]);
    try {
      const l = await listAccessLogs(doc.id);
      setLogs(l);
    } catch {
      addToast("error", "Failed to load access logs");
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const columns: Column<Document>[] = [
    {
      key: "name",
      header: "Document",
      render: (doc) => (
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <FileText className="h-4 w-4 text-accent" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-text-primary">{doc.name}</p>
              {doc.version && doc.version > 1 && (
                <Badge variant="info">v{doc.version}</Badge>
              )}
            </div>
            <p className="text-xs text-text-muted">{formatFileSize(doc.file_size)}</p>
          </div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (doc) => (
        <Badge variant="default">{DOC_TYPE_LABELS[doc.type] || doc.type}</Badge>
      ),
    },
    {
      key: "employee",
      header: "Employee",
      render: (doc) => (
        <span className="text-text-muted">{doc.employee_name || "—"}</span>
      ),
    },
    {
      key: "signature",
      header: "Signature",
      render: (doc) =>
        doc.requires_signature ? (
          doc.signed_at ? (
            <Badge variant="success">Signed {formatDate(doc.signed_at, "MMM d")}</Badge>
          ) : (
            <Badge variant="warning">Needs Signature</Badge>
          )
        ) : (
          <span className="text-xs text-text-muted">N/A</span>
        ),
    },
    {
      key: "expiry",
      header: "Expires",
      render: (doc) => {
        if (!doc.expires_at) return <span className="text-xs text-text-muted">—</span>;
        const isExpiringSoon =
          new Date(doc.expires_at) <=
          new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
        return (
          <span className={`text-xs ${isExpiringSoon ? "text-warning font-medium" : "text-text-muted"}`}>
            {isExpiringSoon && <AlertTriangle className="inline h-3 w-3 mr-1" />}
            {formatDate(doc.expires_at)}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (doc) => (
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(doc);
            }}
            className="p-1.5 rounded text-text-muted hover:bg-surface hover:text-text-primary"
            title="Download"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openVersions(doc);
            }}
            className="p-1.5 rounded text-text-muted hover:bg-surface hover:text-text-primary"
            title="Version history"
          >
            <History className="h-4 w-4" />
          </button>
          {can("manage_documents") && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openLogs(doc);
              }}
              className="p-1.5 rounded text-text-muted hover:bg-surface hover:text-text-primary"
              title="Access log"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {doc.requires_signature && !doc.signed_at && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSign(doc.id);
              }}
              className="p-1.5 rounded text-accent hover:bg-accent/10"
              title="Sign"
            >
              <PenTool className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const docTypeOptions = [
    { value: "", label: "All Types" },
    ...Object.entries(DOC_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })),
  ];

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Documents"
        subtitle={`${data?.total ?? 0} documents`}
        actions={
          can("manage_documents") ? (
            <Button onClick={() => setShowUpload(true)}>
              <Upload className="h-4 w-4" />
              Upload Document
            </Button>
          ) : undefined
        }
      />

      {/* My signatures queue — visible to everyone */}
      <div className="mb-6">
        <h2 className="text-sm font-medium text-text-muted mb-2">
          Awaiting your signature
        </h2>
        <MySignaturesPanel />
      </div>

      <div className="flex gap-3 mb-6">
        <Select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
          options={docTypeOptions}
        />
        <div className="ml-auto">
          <SavedViewsMenu
            views={savedViews.views}
            activeView={savedViews.activeView}
            hasUnsavedChanges={savedViews.hasUnsavedChanges}
            isDefault={savedViews.isDefault}
            onApply={savedViews.applyView}
            onSave={savedViews.saveView}
            onDelete={savedViews.deleteView}
            onRename={savedViews.renameView}
            onUpdateActive={savedViews.updateActiveView}
            onClear={savedViews.clear}
          />
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          keyExtractor={(d) => d.id}
          isLoading={isLoading}
          page={page}
          totalPages={data?.total_pages ?? 1}
          onPageChange={setPage}
          emptyState={
            typeFilter ? (
              <EmptyState
                icon={FileText}
                title="No documents match this filter"
                description="Try a different document type or clear the filter."
                action={{
                  label: "Clear filter",
                  onClick: () => {
                    setTypeFilter("");
                    setPage(1);
                  },
                }}
              />
            ) : can("manage_documents") ? (
              <EmptyState
                icon={FileText}
                title="No documents yet"
                description="Upload offer letters, policies, or anything that needs signing — they'll appear here for the right employees."
                action={{
                  label: "Upload Document",
                  onClick: () => setShowUpload(true),
                }}
              />
            ) : (
              <EmptyState
                icon={FileText}
                title="No documents shared with you yet"
                description="When HR uploads a contract or policy for you to review, it'll show up here."
              />
            )
          }
        />
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUpload}
        onClose={() => {
          setShowUpload(false);
          resetUploadForm();
        }}
        title="Upload Document"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Document Name"
            value={uploadName}
            onChange={(e) => setUploadName(e.target.value)}
            placeholder="e.g., Offer Letter - John Doe"
          />
          <Select
            label="Document Type"
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value)}
            options={Object.entries(DOC_TYPE_LABELS).map(([v, l]) => ({
              value: v,
              label: l,
            }))}
          />
          <Select
            label="Employee"
            value={uploadEmployeeId}
            onChange={(e) => setUploadEmployeeId(e.target.value)}
            options={[
              { value: "", label: "— Select employee —" },
              ...employees.map((e) => ({
                value: e.id,
                label: `${e.first_name} ${e.last_name}${
                  e.title ? ` — ${e.title}` : ""
                }`,
              })),
            ]}
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              File
            </label>
            <input
              type="file"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-accent/10 file:text-accent hover:file:bg-accent/20"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={uploadSig}
              onChange={(e) => setUploadSig(e.target.checked)}
              className="rounded border-border text-accent focus:ring-accent"
            />
            Requires signature
          </label>
          {uploadError && (
            <div
              role="alert"
              className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
            >
              {uploadError}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowUpload(false);
                resetUploadForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload} isLoading={isUploading}>
              Upload
            </Button>
          </div>
        </div>
      </Modal>

      {/* Version history modal */}
      <Modal
        isOpen={!!versionDoc}
        onClose={() => setVersionDoc(null)}
        title={`Version history — ${versionDoc?.name ?? ""}`}
        size="lg"
      >
        <div className="space-y-3">
          {can("manage_documents") && (
            <div className="flex items-end gap-2 border-b border-border pb-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-text-muted mb-1">
                  Upload new version
                </label>
                <input
                  type="file"
                  onChange={(e) =>
                    setNewVersionFile(e.target.files?.[0] || null)
                  }
                  className="w-full text-sm text-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-accent/10 file:text-accent hover:file:bg-accent/20"
                />
              </div>
              <Button onClick={uploadVersion} disabled={!newVersionFile}>
                <Upload className="h-4 w-4" />
                Upload
              </Button>
            </div>
          )}
          {versions.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-6">
              Loading…
            </p>
          ) : (
            <div className="divide-y divide-border/50">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="py-2 flex items-center gap-3 justify-between"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant={v.is_current_version ? "success" : "default"}>
                      v{v.version}
                    </Badge>
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary truncate">
                        {v.name}
                      </p>
                      <p className="text-xs text-text-muted">
                        {formatFileSize(v.file_size)} ·{" "}
                        {formatDate(v.created_at, "MMM d, yyyy HH:mm")}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      downloadDocument(v.id, `${v.name}-v${v.version}`)
                    }
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Access log modal */}
      <Modal
        isOpen={!!logsDoc}
        onClose={() => setLogsDoc(null)}
        title={`Access log — ${logsDoc?.name ?? ""}`}
        size="lg"
      >
        {logs.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            No access recorded yet.
          </p>
        ) : (
          <div className="divide-y divide-border/50 max-h-96 overflow-y-auto">
            {logs.map((l) => (
              <div
                key={l.id}
                className="py-2 flex items-center gap-3 justify-between text-sm"
              >
                <div className="flex items-center gap-2">
                  <Badge
                    variant={l.action === "DOWNLOAD" ? "info" : "default"}
                  >
                    {l.action}
                  </Badge>
                  <span className="text-text-primary">
                    {l.user_email ?? l.user_id}
                  </span>
                </div>
                <span className="text-xs text-text-muted">
                  {formatDate(l.created_at, "MMM d, yyyy HH:mm")}
                </span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
